/**
 * Pure, framework-free predicate that decides whether a Honeybadger notice
 * should leave the browser. app.js registers it via configureHoneybadger().
 *
 * Lives in its own module so it can be unit tested under `node --test` without
 * a browser or build step (mirrors the livewire-419.js pattern).
 *
 * ── Why a list?
 * Each entry represents a class of front-end noise that costs us a GitHub issue
 * but tells us nothing actionable. The 14-day audit summarised in /bigtime
 * triage (see https://github.com/EXACTsports/camps for the bulk-closed issue
 * batch) showed these patterns producing ~70 notices with no real signal.
 * Keep entries narrow — a too-broad pattern will silently mute real bugs.
 */

/**
 * Patterns of notices we suppress. Each entry must specify at least one of:
 *
 *   - `name`            — exact match against `notice.name` (the class/label
 *                         passed as the second arg to `Honeybadger.notify`,
 *                         e.g. `{ name: 'Livewire419Error' }`).
 *   - `messageIncludes` — substring match against `notice.message`. Used for
 *                         browser-generated notices (window.onerror,
 *                         window.onunhandledrejection) where Honeybadger sets
 *                         the message but we can't pick the name.
 *   - `messageMatches`  — RegExp tested against `notice.message`, for when a
 *                         substring can't tell noise from a real failure that
 *                         shares its prefix.
 *
 * `label` is purely descriptive; it never affects matching.
 */
export const IGNORED_NOTICE_PATTERNS = [
    // Explicit notify() from app.js when a Livewire request returns 419
    // (expired CSRF after a deploy or a long-idle tab). The 419 recovery code
    // already reloads the page transparently and preserves form input, so the
    // user impact is nil — the notice was only useful while we were tuning the
    // recovery path. Filter by name so the notify() call can stay in place and
    // be re-enabled by deleting one line of this filter list.
    { label: 'Livewire 419 (expired CSRF; recovered transparently)', name: 'Livewire419Error' },

    // Livewire v3 races against its own DOM teardown on fast navigations.
    { label: 'Livewire DOM-teardown race', messageIncludes: 'Could not find Livewire component in DOM tree' },

    // Benign Chrome/WebKit warning fired when an observer callback forces
    // layout during the same frame. Covers both
    // "ResizeObserver loop completed with undelivered notifications." and the
    // older "ResizeObserver loop limit exceeded".
    { label: 'ResizeObserver loop (benign browser warning)', messageIncludes: 'ResizeObserver loop' },

    // Microsoft Outlook Safe Links and the Microsoft 365 link-preview crawler
    // inject calls into a host bridge (window.external.notify) that doesn't
    // exist in the page context. The resulting unhandled promise rejection
    // has nothing to do with the app — it fires on any healthy page Outlook
    // touches.
    { label: 'Outlook Safe Links host bridge', messageIncludes: 'Object Not Found Matching Id' },

    // A promise rejected with a reason that serializes to an empty object
    // (e.g. a DOMException from a fetch that Safari's tracking prevention
    // aborted — Google Analytics collect calls show up in the breadcrumbs
    // with status 0). The notice carries no app stack frames and no payload,
    // so it is unactionable noise. Exact-string match keeps rejections with
    // a real serialized reason reportable.
    //
    // SDK drift caveat: the `UnhandledPromiseRejectionWarning:` prefix is
    // applied by the Honeybadger SDK's onunhandledrejection wrapper. If a
    // future SDK upgrade renames or drops this prefix, this pattern (and
    // the "Failed to fetch" one below) silently stop matching — failing
    // OPEN (more noise, not less signal). Revisit on SDK upgrades.
    { label: 'Unhandled rejection with empty payload', messageIncludes: 'UnhandledPromiseRejectionWarning: {}' },

    // GA / gtag.js loaded async; clicking a button before the script lands
    // fires this once. The page is fine.
    { label: 'GA gtag not loaded yet', messageIncludes: 'gtag is not defined' },

    // Truncated JSON response from a transient network blip — the page's
    // retry/refresh path handles it. Pattern intentionally specific to the
    // Safari/WebKit "Unexpected EOF" phrasing rather than the broader
    // "Unexpected token '}'" form, because that broader form would also
    // mute genuine bundle/deploy syntax errors in our own code.
    { label: 'Truncated JSON response (transient network)', messageIncludes: 'JSON Parse error: Unexpected EOF' },

    // Fetch that the user (or their browser) aborted: tab closed, network
    // dropped, navigated away mid-request. Scoped to the unhandled-rejection
    // prefix so explicit Honeybadger.notify('Failed to fetch …') calls from
    // reportToHoneybadger() still report. (Same SDK-drift caveat as the
    // empty-payload pattern above.) The SDK keeps the `TypeError: ` the
    // browser puts before the text; the old substring omitted it, so the
    // pattern never matched a real notice (camps#4866). Anchored at both ends
    // so a dynamic-import failure, which shares the prefix, is judged below.
    { label: 'Aborted/cancelled fetch (unhandled rejection)', messageMatches: /^UnhandledPromiseRejectionWarning: (TypeError: )?Failed to fetch$/ },

    // Filament loads its form/table components with import() from
    // /js/filament/…?v=<filament version>. Those files are committed and the
    // URL only changes on a Filament upgrade, so a failed load is the network
    // dropping, not a stale deploy (camps#4866: every import failure on the
    // fault was one of these, all serving 200). Scoped to /js/filament/ so a
    // failed import of one of our own Vite chunks still reports.
    { label: 'Filament component import dropped (network)', messageMatches: /^UnhandledPromiseRejectionWarning: TypeError: Failed to fetch dynamically imported module: \S*\/js\/filament\// },

    // Livewire v3 internal JS reading $wire.entangle on a stub component
    // during morph; the page survives because the next render rebinds.
    { label: 'Livewire entangle on torn-down component', messageIncludes: "Cannot read properties of undefined (reading 'entangle')" },

    // A third-party / browser-extension script re-inserted into the DOM
    // redeclares its own top-level `forms_info`. No code in this repo defines
    // that identifier, so the notice is never ours (camps#8827, 19 issues).
    // Scoped to the identifier so a redeclare of one of OUR globals — a real
    // double-loaded bundle — still reports.
    { label: 'Third-party forms_info redeclare', messageIncludes: "Identifier 'forms_info' has already been declared" },
    // Firefox words the same redeclare differently (camps#13768).
    { label: 'Third-party forms_info redeclare (Firefox)', messageIncludes: 'redeclaration of const forms_info' },

    // Scripts the browser itself injects into the page, none of which this repo
    // defines (camps#4866): Facebook/Instagram's in-app browser probing
    // window._META, a crypto-wallet extension resetting window.ethereum, and
    // Firefox for iOS's reader-mode bridge window.__firefox__. Each is scoped to
    // the injected global so an undefined-object error in our own code still
    // reports.
    { label: 'Meta in-app browser bridge', messageIncludes: 'window._META.IABInnerFrame' },
    { label: 'Crypto-wallet extension', messageIncludes: 'window.ethereum.selectedAddress' },
    { label: 'Firefox iOS reader bridge', messageIncludes: 'window.__firefox__.reader' },
];

/**
 * Return `false` to suppress the notice, `true` to let it through.
 *
 * A beforeNotify handler returning false tells Honeybadger to drop the
 * notice; anything truthy lets it ship. We accept a `notice`-
 * shaped object so this is trivial to unit test without the SDK.
 *
 * @param {{ message?: string, name?: string }} notice
 * @returns {boolean}
 */
export function shouldNotifyHoneybadger(notice) {
    const message = notice?.message ?? '';
    const name = notice?.name ?? '';

    for (const pattern of IGNORED_NOTICE_PATTERNS) {
        if (pattern.name !== undefined && name === pattern.name) {
            return false;
        }
        if (pattern.messageIncludes !== undefined && message.includes(pattern.messageIncludes)) {
            return false;
        }
        if (pattern.messageMatches !== undefined && pattern.messageMatches.test(message)) {
            return false;
        }
    }

    return true;
}

/**
 * Configure the Honeybadger client and register `shouldNotifyHoneybadger`.
 *
 * The SDK never reads a `beforeNotify` key from the configure() options; a
 * handler only runs once registered with `honeybadger.beforeNotify(fn)`. It
 * was passed as an option until camps#8827, so no pattern above suppressed
 * anything in production.
 *
 * @template {{ configure: (options: object) => T, beforeNotify: (handler: Function) => T }} T
 * @param {T} honeybadger
 * @param {object} options
 * @returns {T}
 */
export function configureHoneybadger(honeybadger, options) {
    return honeybadger.configure(options).beforeNotify(shouldNotifyHoneybadger);
}
